// Open Channel Flow in Fluid Dynamics

const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: Channel Cross-Sections & Geometry ──────────────────
function DiagramCrossSections() {
  const W = 900, H = 310;
  const cols = [
    { ox:18,  label:'Rectangular',   w:150 },
    { ox:198, label:'Trapezoidal',   w:150 },
    { ox:378, label:'Triangular (V)', w:150 },
    { ox:558, label:'Circular (pipe)', w:150 },
    { ox:738, label:'Wide / Natural', w:150 },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Common Open Channel Cross-Sections and Geometric Properties</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="dimArr" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#555"/>
          </marker>
          <marker id="dimArrL" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto-start-reverse">
            <polygon points="0 0, 5 3, 0 6" fill="#555"/>
          </marker>
          {/* Water fill pattern */}
          <pattern id="waterPat" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
            <line x1="0" y1="8" x2="8" y2="0" stroke="#aed6f1" strokeWidth="1" opacity="0.5"/>
          </pattern>
        </defs>

        {/* ── 1. Rectangular ── */}
        {(()=>{
          const ox=28, bw=110, oy=80, depth=90, y=100;
          return <>
            <rect x={ox} y={oy+y-depth} width={bw} height={depth} fill="url(#waterPat)" stroke="none"/>
            <line x1={ox} y1={oy} x2={ox} y2={oy+y} stroke="#555" strokeWidth="2.5"/>
            <line x1={ox} y1={oy+y} x2={ox+bw} y2={oy+y} stroke="#555" strokeWidth="2.5"/>
            <line x1={ox+bw} y1={oy} x2={ox+bw} y2={oy+y} stroke="#555" strokeWidth="2.5"/>
            {/* water surface */}
            <line x1={ox} y1={oy+y-depth} x2={ox+bw} y2={oy+y-depth} stroke="#2980b9" strokeWidth="2" strokeDasharray="4,2"/>
            {/* depth arrow */}
            <line x1={ox-14} y1={oy+y} x2={ox-14} y2={oy+y-depth} stroke="#555" strokeWidth="1.2" markerEnd="url(#dimArr)" markerStart="url(#dimArrL)"/>
            <text x={ox-22} y={oy+y-depth/2+4} textAnchor="middle" fontSize="11" fill="#555" transform={`rotate(-90,${ox-22},${oy+y-depth/2+4})`}>y</text>
            {/* width arrow */}
            <line x1={ox} y1={oy+y+16} x2={ox+bw} y2={oy+y+16} stroke="#555" strokeWidth="1.2" markerEnd="url(#dimArr)" markerStart="url(#dimArrL)"/>
            <text x={ox+bw/2} y={oy+y+28} textAnchor="middle" fontSize="11" fill="#555">b</text>
            <text x={ox+bw/2} y={25} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Rectangular</text>
            <rect x={ox-4} y={220} width={bw+8} height={72} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={ox+bw/2} y={234} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">A = by</text>
            <text x={ox+bw/2} y={249} textAnchor="middle" fontSize="11" fill="#444">P = b + 2y</text>
            <text x={ox+bw/2} y={264} textAnchor="middle" fontSize="11" fill="#444">T = b</text>
            <text x={ox+bw/2} y={279} textAnchor="middle" fontSize="11" fill="#444">R = by/(b+2y)</text>
            <text x={ox+bw/2} y={284} textAnchor="middle" fontSize="10" fill="#888">Best when b=2y</text>
          </>;
        })()}

        {/* ── 2. Trapezoidal ── */}
        {(()=>{
          const ox=208, bw=90, oy=80, depth=90, y=100, z=1.0; // z:1 side slope
          const sl = depth * z; // horizontal extent of each side
          const totalW = bw + 2*sl;
          return <>
            <polygon points={`${ox},${oy+y} ${ox+bw+2*sl},${oy+y} ${ox+bw+sl},${oy+y-depth} ${ox+sl},${oy+y-depth}`}
              fill="url(#waterPat)" stroke="none"/>
            <line x1={ox} y1={oy+y} x2={ox+sl} y2={oy+y-depth} stroke="#555" strokeWidth="2.5"/>
            <line x1={ox} y1={oy+y} x2={ox+bw+2*sl} y2={oy+y} stroke="#555" strokeWidth="2.5"/>
            <line x1={ox+bw+2*sl} y1={oy+y} x2={ox+bw+sl} y2={oy+y-depth} stroke="#555" strokeWidth="2.5"/>
            <line x1={ox+sl} y1={oy+y-depth} x2={ox+bw+sl} y2={oy+y-depth} stroke="#2980b9" strokeWidth="2" strokeDasharray="4,2"/>
            {/* slope label */}
            <text x={ox+sl/2-4} y={oy+y-depth/2} fontSize="10" fill="#e07b00" fontWeight="700">1:z</text>
            {/* base b */}
            <line x1={ox+sl} y1={oy+y+16} x2={ox+sl+bw} y2={oy+y+16} stroke="#555" strokeWidth="1.2" markerEnd="url(#dimArr)" markerStart="url(#dimArrL)"/>
            <text x={ox+sl+bw/2} y={oy+y+28} textAnchor="middle" fontSize="11" fill="#555">b</text>
            {/* depth */}
            <line x1={ox-14} y1={oy+y} x2={ox-14} y2={oy+y-depth} stroke="#555" strokeWidth="1.2" markerEnd="url(#dimArr)" markerStart="url(#dimArrL)"/>
            <text x={ox-22} y={oy+y-depth/2+4} textAnchor="middle" fontSize="11" fill="#555" transform={`rotate(-90,${ox-22},${oy+y-depth/2+4})`}>y</text>
            <text x={ox+totalW/2} y={25} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Trapezoidal</text>
            <rect x={ox-4} y={220} width={totalW+8} height={72} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={ox+totalW/2} y={234} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">A = (b + zy)y</text>
            <text x={ox+totalW/2} y={249} textAnchor="middle" fontSize="11" fill="#444">P = b + 2y√(1+z²)</text>
            <text x={ox+totalW/2} y={264} textAnchor="middle" fontSize="11" fill="#444">T = b + 2zy</text>
            <text x={ox+totalW/2} y={279} textAnchor="middle" fontSize="11" fill="#444">R = A/P</text>
            <text x={ox+totalW/2} y={284} textAnchor="middle" fontSize="10" fill="#888">Most efficient: z=1/√3</text>
          </>;
        })()}

        {/* ── 3. Triangular ── */}
        {(()=>{
          const ox=388, oy=80, depth=90, y=100, z=1.2;
          const hw = depth*z;
          return <>
            <polygon points={`${ox+hw},${oy+y} ${ox},${oy+y-depth} ${ox+2*hw},${oy+y-depth}`}
              fill="url(#waterPat)" stroke="none"/>
            <line x1={ox+hw} y1={oy+y} x2={ox} y2={oy+y-depth} stroke="#555" strokeWidth="2.5"/>
            <line x1={ox+hw} y1={oy+y} x2={ox+2*hw} y2={oy+y-depth} stroke="#555" strokeWidth="2.5"/>
            <line x1={ox} y1={oy+y-depth} x2={ox+2*hw} y2={oy+y-depth} stroke="#2980b9" strokeWidth="2" strokeDasharray="4,2"/>
            {/* depth dashed */}
            <line x1={ox+hw} y1={oy+y} x2={ox+hw} y2={oy+y-depth} stroke="#555" strokeWidth="1" strokeDasharray="3,2"/>
            <line x1={ox+hw+14} y1={oy+y} x2={ox+hw+14} y2={oy+y-depth} stroke="#555" strokeWidth="1.2" markerEnd="url(#dimArr)" markerStart="url(#dimArrL)"/>
            <text x={ox+hw+22} y={oy+y-depth/2+4} textAnchor="middle" fontSize="11" fill="#555" transform={`rotate(-90,${ox+hw+22},${oy+y-depth/2+4})`}>y</text>
            <text x={ox+hw} y={25} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Triangular</text>
            <rect x={ox-8} y={220} width={2*hw+16} height={72} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={ox+hw} y={234} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">A = zy²</text>
            <text x={ox+hw} y={249} textAnchor="middle" fontSize="11" fill="#444">P = 2y√(1+z²)</text>
            <text x={ox+hw} y={264} textAnchor="middle" fontSize="11" fill="#444">T = 2zy</text>
            <text x={ox+hw} y={279} textAnchor="middle" fontSize="11" fill="#444">R = zy / (2√(1+z²))</text>
            <text x={ox+hw} y={284} textAnchor="middle" fontSize="10" fill="#888">Best: z=1 (45° sides)</text>
          </>;
        })()}

        {/* ── 4. Circular ── */}
        {(()=>{
          const cx=643, cy=140, r=55, fill_frac=0.65;
          // water fill angle: from bottom
          const theta = Math.acos(1 - 2*fill_frac); // half angle from top
          const wy = cy - r + 2*r*fill_frac;
          return <>
            <circle cx={cx} cy={cy} r={r} fill="none" stroke="#555" strokeWidth="2.5"/>
            {/* water fill using clipPath approximation */}
            <clipPath id="circClip">
              <rect x={cx-r-2} y={wy} width={2*r+4} height={r+(cy-wy)+2}/>
            </clipPath>
            <circle cx={cx} cy={cy} r={r} fill="url(#waterPat)" clipPath="url(#circClip)"/>
            <line x1={cx-r} y1={wy} x2={cx+r} y2={wy} stroke="#2980b9" strokeWidth="2" strokeDasharray="4,2"/>
            {/* diameter */}
            <line x1={cx-r-14} y1={cy-r} x2={cx-r-14} y2={cy+r} stroke="#555" strokeWidth="1.2" markerEnd="url(#dimArr)" markerStart="url(#dimArrL)"/>
            <text x={cx-r-22} y={cy+4} textAnchor="middle" fontSize="11" fill="#555" transform={`rotate(-90,${cx-r-22},${cy+4})`}>D</text>
            {/* depth y */}
            <line x1={cx+r+6} y1={wy} x2={cx+r+6} y2={cy+r} stroke="#555" strokeWidth="1.2" markerEnd="url(#dimArr)" markerStart="url(#dimArrL)"/>
            <text x={cx+r+18} y={cy+r/2+4} fontSize="11" fill="#555">y</text>
            <text x={cx} y={25} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Circular</text>
            <rect x={cx-r-10} y={220} width={2*r+20} height={72} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={cx} y={234} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">θ = 2 arccos(1−2y/D)</text>
            <text x={cx} y={249} textAnchor="middle" fontSize="11" fill="#444">A = D²(θ−sinθ)/8</text>
            <text x={cx} y={264} textAnchor="middle" fontSize="11" fill="#444">P = Dθ/2</text>
            <text x={cx} y={279} textAnchor="middle" fontSize="11" fill="#444">T = D sinθ/2</text>
            <text x={cx} y={284} textAnchor="middle" fontSize="10" fill="#888">Max Q: y/D ≈ 0.94</text>
          </>;
        })()}

        {/* ── 5. Wide / Natural ── */}
        {(()=>{
          const ox=745, bw=130, oy=80, depth=70;
          // irregular bed
          const bed = `M ${ox},${oy+100} C ${ox+20},${oy+105} ${ox+40},${oy+95} ${ox+60},${oy+100} C ${ox+80},${oy+107} ${ox+110},${oy+98} ${ox+bw},${oy+100}`;
          return <>
            <path d={`${bed} L ${ox+bw},${oy+100-depth} L ${ox},${oy+100-depth} Z`} fill="url(#waterPat)"/>
            <path d={bed} fill="none" stroke="#555" strokeWidth="2.5"/>
            <line x1={ox} y1={oy+100} x2={ox} y2={oy+100-depth} stroke="#555" strokeWidth="2.5"/>
            <line x1={ox+bw} y1={oy+100} x2={ox+bw} y2={oy+100-depth} stroke="#555" strokeWidth="2.5"/>
            <line x1={ox} y1={oy+100-depth} x2={ox+bw} y2={oy+100-depth} stroke="#2980b9" strokeWidth="2" strokeDasharray="4,2"/>
            {/* width T */}
            <line x1={ox} y1={oy+100-depth-14} x2={ox+bw} y2={oy+100-depth-14} stroke="#555" strokeWidth="1.2" markerEnd="url(#dimArr)" markerStart="url(#dimArrL)"/>
            <text x={ox+bw/2} y={oy+100-depth-22} textAnchor="middle" fontSize="11" fill="#555">T (top width)</text>
            <text x={ox+bw/2} y={25} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Natural</text>
            <rect x={ox-4} y={220} width={bw+8} height={72} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={ox+bw/2} y={234} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">Hydraulic radius: R = A/P</text>
            <text x={ox+bw/2} y={249} textAnchor="middle" fontSize="11" fill="#444">Hydraulic depth: D = A/T</text>
            <text x={ox+bw/2} y={264} textAnchor="middle" fontSize="11" fill="#444">Top width T measured</text>
            <text x={ox+bw/2} y={279} textAnchor="middle" fontSize="11" fill="#444">at free surface</text>
            <text x={ox+bw/2} y={284} textAnchor="middle" fontSize="10" fill="#888">Surveyed each flood</text>
          </>;
        })()}

        {/* Water surface legend */}
        <line x1={340} y1={295} x2={380} y2={295} stroke="#2980b9" strokeWidth="2" strokeDasharray="4,2"/>
        <text x={384} y={299} fontSize="11" fill="#2980b9">Free water surface</text>
        <rect x={460} y={287} width={18} height={12} fill="url(#waterPat)" stroke="#aed6f1" strokeWidth="0.5"/>
        <text x={482} y={299} fontSize="11" fill="#555">Flow area A</text>
      </svg>
      <div style={S.dCaption}>Open channel cross-sections are characterised by the flow area A, wetted perimeter P (channel boundary in contact with water), top width T (free-surface width), and hydraulic radius R = A/P. R is the key length scale for resistance calculations. The hydraulic depth D = A/T governs wave speed and Froude number. The most hydraulically efficient (minimum P for a given A) section is a semicircle; the most efficient trapezoid has a half-hexagonal shape (z = 1/√3).</div>
    </div>
  );
}

// ── Diagram 2: Froude Number Regimes ─────────────────────────────
function DiagramFroudeRegimes() {
  const W = 900, H = 320;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Froude Number Regimes — Subcritical, Critical and Supercritical Flow</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="frArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="frArrG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60"/>
          </marker>
          <marker id="frArrB" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#2980b9"/>
          </marker>
          <pattern id="wp2" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
            <line x1="0" y1="8" x2="8" y2="0" stroke="#aed6f1" strokeWidth="1.2" opacity="0.6"/>
          </pattern>
        </defs>

        {/* Left: Subcritical (Fr < 1) */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Subcritical Flow  (Fr &lt; 1 — deep, slow)</text>

        {(()=>{
          const bx=25, by=80, bw=PW-30, depth=100, bedY=210;
          // Gentle slope, deep water
          return <>
            {/* Bed */}
            <rect x={bx} y={bedY} width={bw} height={14} rx={2} fill="#c8a46e"/>
            {/* Water body */}
            <rect x={bx} y={bedY-depth} width={bw} height={depth} fill="url(#wp2)"/>
            {/* Water surface — nearly flat, gentle surface waves propagating upstream */}
            <path d={`M ${bx},${bedY-depth} C ${bx+50},${bedY-depth+6} ${bx+100},${bedY-depth-4} ${bx+150},${bedY-depth+2} C ${bx+200},${bedY-depth+6} ${bx+250},${bedY-depth-2} ${bx+bw},${bedY-depth}`}
              fill="none" stroke="#2980b9" strokeWidth="2"/>
            {/* Slow velocity arrows */}
            {[bedY-80, bedY-55, bedY-30].map(y => (
              <line key={y} x1={bx+30} y1={y} x2={bx+80} y2={y}
                stroke="#27ae60" strokeWidth="2" markerEnd="url(#frArrG)"/>
            ))}
            <text x={bx+85} y={bedY-52} fontSize="12" fill="#27ae60" fontWeight="700">V (slow)</text>
            {/* Surface wave propagates BOTH ways */}
            <line x1={bx+180} y1={bedY-depth-18} x2={bx+100} y2={bedY-depth-18}
              stroke="#9b59b6" strokeWidth="2" markerEnd="url(#frArrB)"/>
            <line x1={bx+180} y1={bedY-depth-18} x2={bx+260} y2={bedY-depth-18}
              stroke="#9b59b6" strokeWidth="2" markerEnd="url(#frArrB)"/>
            <text x={bx+180} y={bedY-depth-26} textAnchor="middle" fontSize="11" fill="#9b59b6" fontWeight="700">Wave travels both ways</text>

            {/* Depth label */}
            <line x1={bx+bw+12} y1={bedY} x2={bx+bw+12} y2={bedY-depth}
              stroke="#555" strokeWidth="1.2" markerEnd="url(#frArr)" markerStart="url(#frArr)"/>
            <text x={bx+bw+24} y={bedY-depth/2+4} fontSize="11" fill="#555">y (deep)</text>

            {/* Fr info */}
            <rect x={bx} y={bedY+18} width={bw} height={65} rx={3} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1"/>
            <text x={bx+8} y={bedY+33} fontSize="12" fill="#145214" fontWeight="700">Fr = V/√(gD) &lt; 1</text>
            <text x={bx+8} y={bedY+49} fontSize="11" fill="#444">Flow speed &lt; wave speed (c = √(gD))</text>
            <text x={bx+8} y={bedY+63} fontSize="11" fill="#444">Disturbances travel upstream — downstream</text>
            <text x={bx+8} y={bedY+77} fontSize="11" fill="#444">controls affect upstream (like subsonic flow)</text>
          </>;
        })()}

        {/* Right: Supercritical (Fr > 1) */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Supercritical Flow  (Fr &gt; 1 — shallow, fast)</text>

        {(()=>{
          const bx=PW+35, by=80, bw=PW-30, depth=40, bedY=210;
          return <>
            {/* Steep bed */}
            <path d={`M ${bx},${bedY} L ${bx+bw},${bedY-25}`} fill="none" stroke="none"/>
            <rect x={bx} y={bedY-8} width={bw} height={18} rx={2} fill="#c8a46e"
              transform={`rotate(-5, ${bx}, ${bedY})`}/>
            {/* Shallow fast water */}
            <rect x={bx} y={bedY-depth-8} width={bw} height={depth} fill="url(#wp2)"
              transform={`rotate(-5, ${bx}, ${bedY})`}/>
            {/* Surface — rough broken */}
            <path d={`M ${bx},${bedY-depth-8} C ${bx+30},${bedY-depth-14} ${bx+60},${bedY-depth-4} ${bx+90},${bedY-depth-12} C ${bx+120},${bedY-depth-18} ${bx+160},${bedY-depth-6} ${bx+200},${bedY-depth-14} C ${bx+230},${bedY-depth-20} ${bx+bw-10},${bedY-depth-10} ${bx+bw},${bedY-depth-8}`}
              fill="none" stroke="#2980b9" strokeWidth="2"/>
            {/* Fast velocity arrows */}
            {[bedY-depth+10, bedY-depth+25, bedY-depth+35].map(y => (
              <line key={y} x1={bx+20} y1={y} x2={bx+100} y2={y}
                stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#frArr)"/>
            ))}
            <text x={bx+105} y={bedY-depth+25} fontSize="12" fill="#c0392b" fontWeight="700">V (fast)</text>
            {/* Surface wave — downstream only */}
            <line x1={bx+210} y1={bedY-depth-32} x2={bx+280} y2={bedY-depth-32}
              stroke="#9b59b6" strokeWidth="2" markerEnd="url(#frArrB)"/>
            <text x={bx+245} y={bedY-depth-40} textAnchor="middle" fontSize="11" fill="#9b59b6" fontWeight="700">Wave downstream only</text>
            <text x={bx+245} y={bedY-depth-26} textAnchor="middle" fontSize="11" fill="#9b59b6">(swept away)</text>

            {/* Depth label */}
            <line x1={bx+bw+12} y1={bedY-8} x2={bx+bw+12} y2={bedY-depth-8}
              stroke="#555" strokeWidth="1.2" markerEnd="url(#frArr)" markerStart="url(#frArr)"/>
            <text x={bx+bw+24} y={bedY-depth/2+4} fontSize="11" fill="#555">y (shallow)</text>

            {/* Fr info */}
            <rect x={bx} y={bedY+18} width={bw} height={65} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
            <text x={bx+8} y={bedY+33} fontSize="12" fill="#7B0D0D" fontWeight="700">Fr = V/√(gD) &gt; 1</text>
            <text x={bx+8} y={bedY+49} fontSize="11" fill="#444">Flow speed &gt; wave speed</text>
            <text x={bx+8} y={bedY+63} fontSize="11" fill="#444">Disturbances cannot travel upstream —</text>
            <text x={bx+8} y={bedY+77} fontSize="11" fill="#444">downstream conditions cannot affect upstream</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>The Froude number Fr = V/√(gD) (flow speed / shallow-wave speed) is the governing parameter of open channel flow — precisely analogous to the Mach number in compressible flow. In subcritical flow (Fr &lt; 1) the flow is deep and slow; small-amplitude surface waves can propagate upstream, so downstream conditions (weirs, gates) control the channel. In supercritical flow (Fr &gt; 1) the flow is shallow and fast; no wave can travel upstream — conditions are set by what happens upstream. The analogy is exact: subcritical ↔ subsonic, supercritical ↔ supersonic, hydraulic jump ↔ normal shock.</div>
    </div>
  );
}

// ── Diagram 3: Specific Energy Diagram ───────────────────────────
function DiagramSpecificEnergy() {
  const W = 900, H = 330;

  // E = y + V²/2g = y + q²/(2gy²)  for rectangular channel (q = Q/b)
  // For fixed q, plot y vs E
  const q = 2.0; // m²/s unit discharge
  const g = 9.81;
  const pts_sub = [], pts_sup = [];
  for (let y = 0.15; y <= 4.0; y += 0.02) {
    const E = y + q*q/(2*g*y*y);
    if (E < 6) {
      if (y >= 0.74) pts_sub.push({y, E});  // subcritical branch
      else           pts_sup.push({y, E});   // supercritical branch
    }
  }
  // critical depth: yc = (q²/g)^(1/3)
  const yc = Math.pow(q*q/g, 1/3);
  const Ec = yc + q*q/(2*g*yc*yc);

  // Map: E [0..6] → x [80..780],  y [0..4] → ysvg [280..30]
  const ex = (E) => 80 + (E/6)*700;
  const ey = (y) => 280 - (y/4)*240;

  const subPath = pts_sub.map(({y,E},i) => `${i===0?'M':'L'} ${ex(E)},${ey(y)}`).join(' ');
  const supPath = pts_sup.map(({y,E},i) => `${i===0?'M':'L'} ${ex(E)},${ey(y)}`).join(' ');

  // E=y line (45°)
  const diagPath = `M ${ex(0)},${ey(0)} L ${ex(4)},${ey(4)}`;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Specific Energy Diagram — y vs E for Fixed Discharge q (rectangular channel)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="seArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#333"/>
          </marker>
          <marker id="seArrR" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
        </defs>

        {/* Axes */}
        <line x1={80} y1={290} x2={800} y2={290} stroke="#333" strokeWidth="1.8" markerEnd="url(#seArr)"/>
        <line x1={80} y1={290} x2={80}  y2={20}  stroke="#333" strokeWidth="1.8" markerEnd="url(#seArr)"/>
        <text x={808} y={294} fontSize="13" fill="#333" fontWeight="700">E (m)</text>
        <text x={68}  y={16}  fontSize="13" fill="#333" fontWeight="700">y (m)</text>

        {/* X ticks */}
        {[0,1,2,3,4,5,6].map(v => (
          <g key={v}>
            <line x1={ex(v)} y1={287} x2={ex(v)} y2={293} stroke="#333" strokeWidth="1.2"/>
            <text x={ex(v)} y={305} textAnchor="middle" fontSize="11" fill="#555">{v}</text>
          </g>
        ))}
        {/* Y ticks */}
        {[0,1,2,3,4].map(v => (
          <g key={v}>
            <line x1={77} y1={ey(v)} x2={83} y2={ey(v)} stroke="#333" strokeWidth="1.2"/>
            <text x={72} y={ey(v)+4} textAnchor="end" fontSize="11" fill="#555">{v}</text>
          </g>
        ))}

        {/* E = y line (potential energy only, no velocity) */}
        <path d={diagPath} fill="none" stroke="#888" strokeWidth="1.5" strokeDasharray="6,3"/>
        <text x={ex(3.2)+6} y={ey(3.2)-8} fontSize="11" fill="#888" fontStyle="italic">E = y  (V=0 limit)</text>

        {/* Specific energy curves */}
        <path d={subPath} fill="none" stroke="#27ae60" strokeWidth="2.8"/>
        <path d={supPath} fill="none" stroke="#c0392b" strokeWidth="2.8"/>

        {/* Critical point */}
        <circle cx={ex(Ec)} cy={ey(yc)} r={6} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
        <line x1={ex(Ec)} y1={ey(yc)} x2={ex(Ec)} y2={290} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,3"/>
        <line x1={80} y1={ey(yc)} x2={ex(Ec)} y2={ey(yc)} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,3"/>
        <text x={ex(Ec)+6} y={ey(yc)-8} fontSize="12" fill="#e07b00" fontWeight="700">Critical point</text>
        <text x={ex(Ec)+6} y={ey(yc)+6} fontSize="11" fill="#e07b00">E_c = {Ec.toFixed(2)} m</text>
        <text x={68} y={ey(yc)+4} textAnchor="end" fontSize="11" fill="#e07b00">y_c={yc.toFixed(2)}</text>
        <text x={ex(Ec)} y={302} textAnchor="middle" fontSize="10" fill="#e07b00">E_c</text>

        {/* Region labels */}
        <text x={ex(4.5)} y={ey(3.2)} fontSize="13" fill="#27ae60" fontWeight="700">Subcritical</text>
        <text x={ex(4.5)} y={ey(3.2)+16} fontSize="12" fill="#27ae60">(Fr &lt; 1, upper branch)</text>
        <text x={ex(3.0)} y={ey(0.5)} fontSize="13" fill="#c0392b" fontWeight="700">Supercritical</text>
        <text x={ex(3.0)} y={ey(0.5)+16} fontSize="12" fill="#c0392b">(Fr &gt; 1, lower branch)</text>

        {/* Alternate depths annotation */}
        {(()=>{
          const Efix = 3.5;
          // find sub and sup depths for this E
          const ySub = pts_sub.find(p => Math.abs(p.E - Efix) < 0.06);
          const ySup = pts_sup.find(p => Math.abs(p.E - Efix) < 0.06);
          if (!ySub || !ySup) return null;
          return <>
            <line x1={ex(Efix)} y1={ey(ySub.y)} x2={ex(Efix)} y2={ey(ySup.y)}
              stroke="#9b59b6" strokeWidth="2" strokeDasharray="4,2"/>
            <circle cx={ex(Efix)} cy={ey(ySub.y)} r={4} fill="#27ae60"/>
            <circle cx={ex(Efix)} cy={ey(ySup.y)} r={4} fill="#c0392b"/>
            <text x={ex(Efix)+6} y={ey(ySub.y)+4} fontSize="11" fill="#27ae60">y₁ (subcritical)</text>
            <text x={ex(Efix)+6} y={ey(ySup.y)+4} fontSize="11" fill="#c0392b">y₂ (supercritical)</text>
            <text x={ex(Efix)+6} y={ey((ySub.y+ySup.y)/2)} fontSize="11" fill="#9b59b6">Alternate depths</text>
            <text x={ex(Efix)+6} y={ey((ySub.y+ySup.y)/2)+14} fontSize="11" fill="#9b59b6">same E, same Q</text>
          </>;
        })()}

        {/* Minimum E label */}
        <text x={140} y={20} fontSize="12" fill="#e07b00" fontWeight="700">Minimum specific energy occurs at critical depth</text>
        <text x={140} y={35} fontSize="11" fill="#444">For fixed Q: E_min = (3/2) y_c  (rectangular);  y_c = (q²/g)^(1/3)</text>

        {/* Legend */}
        <line x1={590} y1={30} x2={620} y2={30} stroke="#27ae60" strokeWidth="2.8"/>
        <text x={626} y={34} fontSize="11" fill="#27ae60">Subcritical branch</text>
        <line x1={590} y1={48} x2={620} y2={48} stroke="#c0392b" strokeWidth="2.8"/>
        <text x={626} y={52} fontSize="11" fill="#c0392b">Supercritical branch</text>
        <circle cx={605} cy={66} r={5} fill="#e07b00"/>
        <text x={626} y={70} fontSize="11" fill="#e07b00">Critical point (Fr=1)</text>
      </svg>
      <div style={S.dCaption}>The specific energy E = y + V²/(2g) = y + q²/(2gy²) is the energy per unit weight relative to the channel bed. For a fixed discharge q, plotting depth y against E gives a characteristic curve with two branches meeting at the critical depth y_c = (q²/g)^(1/3) where E is minimum. The upper branch is subcritical; the lower is supercritical. For any E above E_min there are two possible depths — the alternate depths — that carry the same discharge at the same specific energy. A channel transition (hump, constriction) navigates this diagram.</div>
    </div>
  );
}

// ── Diagram 4: Hydraulic Jump ────────────────────────────────────
function DiagramHydraulicJump() {
  const W = 900, H = 310;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Hydraulic Jump — Supercritical to Subcritical Transition</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="hjArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="hjArrG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60"/>
          </marker>
          <marker id="hjArrB" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#2980b9"/>
          </marker>
          <pattern id="wp3" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
            <line x1="0" y1="8" x2="8" y2="0" stroke="#aed6f1" strokeWidth="1.2" opacity="0.6"/>
          </pattern>
        </defs>

        {/* Channel bed */}
        <rect x={20} y={240} width={860} height={14} rx={2} fill="#c8a46e"/>
        <line x1={20} y1={240} x2={880} y2={240} stroke="#8B6914" strokeWidth="1"/>

        {/* Upstream: shallow fast flow */}
        <rect x={20} y={200} width={250} height={40} fill="url(#wp3)"/>
        <line x1={20} y1={200} x2={250} y2={200} stroke="#2980b9" strokeWidth="2.5"/>

        {/* Velocity arrows upstream — fast, low */}
        {[210,223,235].map(y => (
          <line key={y} x1={40} y1={y} x2={110} y2={y}
            stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#hjArr)"/>
        ))}
        <text x={60} y={193} fontSize="12" fill="#c0392b" fontWeight="700">V₁ (fast)</text>

        {/* Jump zone — turbulent, roller */}
        {/* Rising water surface through jump */}
        <path d={`M 250,200 C 280,195 300,175 320,160 C 340,148 360,142 400,138`}
          fill="none" stroke="#2980b9" strokeWidth="2.5"/>
        {/* Jump fill */}
        <path d={`M 250,200 C 280,195 300,175 320,160 C 340,148 360,142 400,138 L 400,240 L 250,240 Z`}
          fill="url(#wp3)" opacity="0.9"/>
        {/* Roller / eddies */}
        {[[280,185,18],[310,168,14],[340,155,11],[265,210,12]].map(([cx,cy,r],i)=>(
          <ellipse key={i} cx={cx} cy={cy} rx={r} ry={r*0.7}
            fill="none" stroke="#fff" strokeWidth="1.5" opacity="0.7"
            strokeDasharray={`${r*0.8},${r*0.5}`}/>
        ))}
        <text x={295} y={225} fontSize="12" fill="#fff" fontWeight="700">ROLLER</text>
        <text x={295} y={240} fontSize="10" fill="#ddd">(turbulent)</text>

        {/* Downstream: deep slow flow */}
        <rect x={400} y={138} width={462} height={102} fill="url(#wp3)"/>
        <line x1={400} y1={138} x2={862} y2={138} stroke="#2980b9" strokeWidth="2.5"/>

        {/* Velocity arrows downstream — slow, deep */}
        {[163,185,207,225].map(y => (
          <line key={y} x1={460} y1={y} x2={510} y2={y}
            stroke="#27ae60" strokeWidth="2" markerEnd="url(#hjArrG)"/>
        ))}
        <text x={520} y={195} fontSize="12" fill="#27ae60" fontWeight="700">V₂ (slow)</text>

        {/* Depth annotations */}
        <line x1={235} y1={200} x2={235} y2={240} stroke="#c0392b" strokeWidth="2" markerEnd="url(#hjArr)" markerStart="url(#hjArr)"/>
        <text x={208} y={224} fontSize="12" fill="#c0392b" fontWeight="700">y₁</text>
        <text x={190} y={214} fontSize="11" fill="#c0392b">Fr₁ &gt; 1</text>

        <line x1={845} y1={138} x2={845} y2={240} stroke="#27ae60" strokeWidth="2" markerEnd="url(#hjArrG)" markerStart="url(#hjArrG)"/>
        <text x={852} y={195} fontSize="12" fill="#27ae60" fontWeight="700">y₂</text>
        <text x={852} y={210} fontSize="11" fill="#27ae60">Fr₂ &lt; 1</text>

        {/* Jump location marker */}
        <line x1={250} y1={130} x2={250} y2={254} stroke="#e07b00" strokeWidth="2" strokeDasharray="4,3"/>
        <text x={253} y={125} fontSize="12" fill="#e07b00" fontWeight="700">Jump location</text>
        <text x={253} y={139} fontSize="11" fill="#e07b00">(Fr = 1 inside)</text>

        {/* Energy loss arrow */}
        <line x1={150} y1={95} x2={550} y2={95} stroke="#9b59b6" strokeWidth="2" markerEnd="url(#hjArr)"/>
        <text x={350} y={88} textAnchor="middle" fontSize="13" fill="#9b59b6" fontWeight="700">Energy lost as turbulence (ΔE = head loss)</text>
        <text x={350} y={104} textAnchor="middle" fontSize="11" fill="#9b59b6">ΔE = (y₂ − y₁)³ / (4 y₁ y₂)</text>

        {/* Sequent depth relation */}
        <rect x={580} y={14} width={300} height={110} rx={4} fill="#fff" stroke="#f0cece"/>
        <text x={730} y={32} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Sequent (Conjugate) Depths</text>
        {[
          {y:50,  bg:'#fff5f5', bc:'#c0392b', t1:'Depth ratio:',    t2:'y₂/y₁ = ½(√(1+8Fr₁²) − 1)'},
          {y:76,  bg:'#fff5e6', bc:'#e07b00', t1:'Energy loss:',    t2:'ΔE = (y₂−y₁)³/(4y₁y₂)'},
          {y:102, bg:'#f0fff4', bc:'#27ae60', t1:'Relative loss:',  t2:'ΔE/E₁ = 1 − (y₂/y₁)·(Fr₂/Fr₁)²/(Fr₁²/1)'},
        ].map(({y,bg,bc,t1,t2}) => (
          <g key={y}>
            <rect x={586} y={y} width={287} height={22} rx={2} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={592} y={y+9} fontSize="10" fill="#333" fontWeight="700">{t1}</text>
            <text x={592} y={y+19} fontSize="10" fill="#444" fontFamily="'Courier New',monospace">{t2}</text>
          </g>
        ))}

        {/* Jump types label */}
        <rect x={20} y={260} width={540} height={38} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={26} y={275} fontSize="11" fill="#7B0D0D" fontWeight="700">Jump Types by Fr₁:  </text>
        <text x={26} y={291} fontSize="11" fill="#444">1.0–1.7: Undular  |  1.7–2.5: Weak  |  2.5–4.5: Oscillating  |  4.5–9: Steady  |  &gt;9: Strong</text>
      </svg>
      <div style={S.dCaption}>The hydraulic jump is the open-channel analogue of a normal shock: supercritical flow abruptly transitions to subcritical with a sudden rise in depth and loss of energy. It is irreversible — momentum is conserved but mechanical energy is dissipated as heat and turbulent mixing. The sequent depth ratio y₂/y₁ = ½(√(1+8Fr₁²) − 1) is derived from conservation of mass and momentum (not energy). Hydraulic jumps are deliberately created in stilling basins below spillways to dissipate the kinetic energy of flood flows before they can scour the downstream channel.</div>
    </div>
  );
}

// ── Diagram 5: Gradually Varied Flow Profiles ────────────────────
function DiagramGVFProfiles() {
  const W = 900, H = 340;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Gradually Varied Flow — Water Surface Profiles (M, S, C, H, A curves)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="gvfArr" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#c0392b"/>
          </marker>
          <pattern id="wp4" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
            <line x1="0" y1="8" x2="8" y2="0" stroke="#aed6f1" strokeWidth="1.2" opacity="0.5"/>
          </pattern>
        </defs>

        {/* Left half: Mild slope (M profiles) */}
        <rect x={10} y={10} width={420} height={320} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={220} y={26} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Mild Slope  (S₀ &lt; Sc — yn &gt; yc)</text>

        {(()=>{
          const bx=25, bw=390, bedY=300, bedSlope=0.01;
          // Reference lines: normal depth yn, critical depth yc
          const yn=80, yc=50;
          // Bed line
          const bedEnd = bedY - bw*bedSlope;
          return <>
            {/* Bed */}
            <line x1={bx} y1={bedY} x2={bx+bw} y2={bedEnd} stroke="#c8a46e" strokeWidth="4"/>
            {/* Normal depth line */}
            <line x1={bx} y1={bedY-yn} x2={bx+bw} y2={bedEnd-yn}
              stroke="#27ae60" strokeWidth="1.5" strokeDasharray="8,4" opacity="0.8"/>
            <text x={bx+bw+4} y={bedEnd-yn+4} fontSize="11" fill="#27ae60" fontWeight="700">yn</text>
            {/* Critical depth line */}
            <line x1={bx} y1={bedY-yc} x2={bx+bw} y2={bedEnd-yc}
              stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,4" opacity="0.8"/>
            <text x={bx+bw+4} y={bedEnd-yc+4} fontSize="11" fill="#e07b00" fontWeight="700">yc</text>

            {/* M1 profile: y > yn > yc — backwater curve, approaches yn asymptotically */}
            <path d={`M ${bx},${bedY-yn-45} C ${bx+80},${bedY-yn-35} ${bx+180},${bedY-yn-18} ${bx+bw},${bedEnd-yn-3}`}
              fill="none" stroke="#2980b9" strokeWidth="2.5"/>
            <path d={`M ${bx},${bedY-yn-45} C ${bx+80},${bedY-yn-35} ${bx+180},${bedY-yn-18} ${bx+bw},${bedEnd-yn-3} L ${bx+bw},${bedEnd} L ${bx},${bedY} Z`}
              fill="url(#wp4)" opacity="0.7"/>
            <text x={bx+40} y={bedY-yn-52} fontSize="12" fill="#2980b9" fontWeight="700">M1</text>
            <text x={bx+40} y={bedY-yn-38} fontSize="11" fill="#2980b9">y &gt; yn &gt; yc</text>
            <text x={bx+40} y={bedY-yn-24} fontSize="10" fill="#444">(backwater — dam/weir)</text>

            {/* M2 profile: yn > y > yc — drawdown */}
            <path d={`M ${bx},${bedY-yn+22} C ${bx+100},${bedY-yn+28} ${bx+200},${bedY-yc+10} ${bx+bw},${bedEnd-yc+2}`}
              fill="none" stroke="#9b59b6" strokeWidth="2.5"/>
            <text x={bx+bw-60} y={bedEnd-yc+18} fontSize="12" fill="#9b59b6" fontWeight="700">M2</text>
            <text x={bx+bw-100} y={bedEnd-yc+32} fontSize="11" fill="#9b59b6">yn &gt; y &gt; yc</text>
            <text x={bx+bw-120} y={bedEnd-yc+46} fontSize="10" fill="#444">(drawdown to falls)</text>

            {/* M3 profile: yn > yc > y — steep portion just downstream of gate */}
            <path d={`M ${bx},${bedY-yc+32} C ${bx+80},${bedY-yc+22} ${bx+180},${bedY-yc+12} ${bx+260},${bedEnd-yc+18}`}
              fill="none" stroke="#c0392b" strokeWidth="2.5"/>
            <text x={bx+50} y={bedY-yc+50} fontSize="12" fill="#c0392b" fontWeight="700">M3</text>
            <text x={bx+50} y={bedY-yc+64} fontSize="11" fill="#c0392b">yn &gt; yc &gt; y</text>
            <text x={bx+50} y={bedY-yc+78} fontSize="10" fill="#444">(→ hydraulic jump)</text>
          </>;
        })()}

        {/* Right half: Steep slope (S profiles) */}
        <rect x={450} y={10} width={440} height={320} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={670} y={26} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Steep Slope  (S₀ &gt; Sc — yn &lt; yc)</text>

        {(()=>{
          const bx=465, bw=410, bedY=300, bedSlope=0.06;
          const yn=35, yc=65;
          const bedEnd = bedY - bw*bedSlope;
          return <>
            <line x1={bx} y1={bedY} x2={bx+bw} y2={bedEnd} stroke="#c8a46e" strokeWidth="4"/>
            {/* Normal depth line */}
            <line x1={bx} y1={bedY-yn} x2={bx+bw} y2={bedEnd-yn}
              stroke="#27ae60" strokeWidth="1.5" strokeDasharray="8,4" opacity="0.8"/>
            <text x={bx+bw+4} y={bedEnd-yn+4} fontSize="11" fill="#27ae60" fontWeight="700">yn</text>
            {/* Critical depth line */}
            <line x1={bx} y1={bedY-yc} x2={bx+bw} y2={bedEnd-yc}
              stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,4" opacity="0.8"/>
            <text x={bx+bw+4} y={bedEnd-yc+4} fontSize="11" fill="#e07b00" fontWeight="700">yc</text>

            {/* S1: y > yc > yn */}
            <path d={`M ${bx},${bedY-yc-50} C ${bx+100},${bedY-yc-40} ${bx+220},${bedY-yc-20} ${bx+bw},${bedEnd-yc-4}`}
              fill="none" stroke="#2980b9" strokeWidth="2.5"/>
            <path d={`M ${bx},${bedY-yc-50} C ${bx+100},${bedY-yc-40} ${bx+220},${bedY-yc-20} ${bx+bw},${bedEnd-yc-4} L ${bx+bw},${bedEnd} L ${bx},${bedY} Z`}
              fill="url(#wp4)" opacity="0.6"/>
            <text x={bx+30} y={bedY-yc-58} fontSize="12" fill="#2980b9" fontWeight="700">S1</text>
            <text x={bx+30} y={bedY-yc-44} fontSize="11" fill="#2980b9">y &gt; yc &gt; yn</text>
            <text x={bx+30} y={bedY-yc-30} fontSize="10" fill="#444">(backwater on steep slope)</text>

            {/* S2: yc > y > yn — drawdown on steep slope */}
            <path d={`M ${bx},${bedY-yc+22} C ${bx+100},${bedY-yc+18} ${bx+220},${bedY-yn-10} ${bx+bw},${bedEnd-yn-3}`}
              fill="none" stroke="#9b59b6" strokeWidth="2.5"/>
            <text x={bx+30} y={bedY-yc+40} fontSize="12" fill="#9b59b6" fontWeight="700">S2</text>
            <text x={bx+30} y={bedY-yc+54} fontSize="11" fill="#9b59b6">yc &gt; y &gt; yn</text>

            {/* S3: yc > yn > y */}
            <path d={`M ${bx},${bedY-yn+25} C ${bx+120},${bedY-yn+18} ${bx+250},${bedY-yn+10} ${bx+bw},${bedEnd-yn-2}`}
              fill="none" stroke="#c0392b" strokeWidth="2.5"/>
            <text x={bx+220} y={bedY-yn+38} fontSize="12" fill="#c0392b" fontWeight="700">S3</text>
            <text x={bx+220} y={bedY-yn+52} fontSize="11" fill="#c0392b">yc &gt; yn &gt; y</text>
          </>;
        })()}

        {/* Legend at bottom */}
        <line x1={18} y1={335} x2={50} y2={335} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="8,4"/>
        <text x={54} y={339} fontSize="11" fill="#27ae60">Normal depth yn (uniform flow)</text>
        <line x1={240} y1={335} x2={272} y2={335} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,4"/>
        <text x={276} y={339} fontSize="11" fill="#e07b00">Critical depth yc (Fr=1)</text>
        <text x={500} y={339} fontSize="11" fill="#555">Flow direction → rightward on all profiles</text>
      </svg>
      <div style={S.dCaption}>Gradually varied flow (GVF) occurs when depth changes slowly along a channel so that streamlines are nearly parallel and hydrostatic pressure holds. The governing ODE dy/dx = (S₀ − Sf)/(1 − Fr²) produces 12 standard profile types (M1–M3, S1–S3, C1–C3, H2–H3, A2–A3) depending on slope classification and whether depth is above or below normal and critical. M1 (backwater curve behind a dam) and M2 (drawdown to a free overfall) are the most common in engineering. Profiles must be computed numerically by integrating upstream for subcritical flow and downstream for supercritical.</div>
    </div>
  );
}

// ── Diagram 6: Manning's Equation & Uniform Flow ─────────────────
function DiagramUniformFlow() {
  const W = 900, H = 300;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Uniform Flow — Manning's Equation and Channel Resistance</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <pattern id="wp5" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
            <line x1="0" y1="8" x2="8" y2="0" stroke="#aed6f1" strokeWidth="1.2" opacity="0.6"/>
          </pattern>
          <marker id="ufArr" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#c0392b"/>
          </marker>
          <marker id="ufArrG" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#27ae60"/>
          </marker>
        </defs>

        {/* Channel side view */}
        {(()=>{
          const bx=30, bedY=220, bw=360;
          const slope=0.025, depth=90;
          const bedEnd=bedY-bw*slope;
          const wsEnd=bedEnd-depth;
          return <>
            {/* Bed */}
            <line x1={bx} y1={bedY} x2={bx+bw} y2={bedEnd} stroke="#c8a46e" strokeWidth="5"/>
            {/* Hatch below bed */}
            {[0,1,2,3,4,5,6,7,8,9].map(i=>(
              <line key={i} x1={bx+i*40} y1={bedY-i*slope*40+8} x2={bx+i*40+16} y2={bedY-i*slope*40+24}
                stroke="#a0804a" strokeWidth="1" opacity="0.6"/>
            ))}
            {/* Water body */}
            <path d={`M ${bx},${bedY} L ${bx},${bedY-depth} L ${bx+bw},${wsEnd} L ${bx+bw},${bedEnd} Z`}
              fill="url(#wp5)"/>
            {/* Water surface */}
            <line x1={bx} y1={bedY-depth} x2={bx+bw} y2={wsEnd}
              stroke="#2980b9" strokeWidth="2.5"/>

            {/* Slope angle */}
            <path d={`M ${bx+50},${bedY} A 50 50 0 0 0 ${bx+50+50*Math.cos(Math.atan(slope))},${bedY-50*Math.sin(Math.atan(slope))}`}
              fill="none" stroke="#e07b00" strokeWidth="1.5"/>
            <text x={bx+62} y={bedY-10} fontSize="12" fill="#e07b00" fontWeight="700">S₀</text>
            <text x={bx+58} y={bedY+14} fontSize="11" fill="#e07b00">(bed slope)</text>

            {/* Depth arrow */}
            <line x1={bx-14} y1={bedY} x2={bx-14} y2={bedY-depth}
              stroke="#555" strokeWidth="1.2" markerEnd="url(#ufArr)" markerStart="url(#ufArr)"/>
            <text x={bx-22} y={bedY-depth/2+4} textAnchor="middle" fontSize="11" fill="#555"
              transform={`rotate(-90,${bx-22},${bedY-depth/2+4})`}>y (depth)</text>

            {/* Flow arrows */}
            {[bedY-75, bedY-55, bedY-35].map(y => (
              <line key={y} x1={bx+100} y1={y} x2={bx+160} y2={y-slope*60}
                stroke="#c0392b" strokeWidth="2" markerEnd="url(#ufArr)"/>
            ))}
            <text x={bx+170} y={bedY-80} fontSize="12" fill="#c0392b" fontWeight="700">V (mean velocity)</text>

            {/* Wetted perimeter label */}
            <path d={`M ${bx+220},${bedY-5} L ${bx+220},${bedY-depth-5}`}
              fill="none" stroke="#9b59b6" strokeWidth="2.5"/>
            <text x={bx+226} y={bedY-depth/2} fontSize="11" fill="#9b59b6" fontWeight="700">P (wetted perimeter)</text>

            {/* R = A/P label */}
            <text x={bx+130} y={bedY-depth/3} textAnchor="middle" fontSize="13" fill="#2980b9" fontWeight="700">R = A/P</text>
          </>;
        })()}

        {/* Manning's formula panel */}
        <rect x={420} y={15} width={460} height={270} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={650} y={34} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Manning's Equation  (1889)</text>

        {[
          { y:50,  bg:'#FEF0F0', bc:'#c0392b', lines:['Mean velocity:','V = (1/n) · R^(2/3) · S₀^(1/2)'] },
          { y:86,  bg:'#fff5e6', bc:'#e07b00', lines:['Discharge:','Q = A·V = (A/n)·R^(2/3)·S₀^(1/2)'] },
          { y:122, bg:'#f0fff4', bc:'#27ae60', lines:['Normal depth:','Implicit — solve Q = f(y) numerically'] },
          { y:158, bg:'#f5e6ff', bc:'#9b59b6', lines:['Chezy (earlier form):','V = C√(RS₀)   where C = R^(1/6)/n'] },
          { y:194, bg:'#FEF0F0', bc:'#c0392b', lines:['Darcy–Weisbach form:','Sf = f·V²/(8gR)   f = friction factor'] },
        ].map(({y,bg,bc,lines}) => (
          <g key={y}>
            <rect x={428} y={y} width={444} height={32} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={434} y={y+13} fontSize="11" fill="#333" fontWeight="700">{lines[0]}</text>
            <text x={434} y={y+26} fontSize="12" fill="#444" fontFamily="'Courier New',monospace">{lines[1]}</text>
          </g>
        ))}

        {/* Manning's n table */}
        <rect x={428} y={234} width={444} height={46} rx={3} fill="#fff5f5" stroke="#f0cece"/>
        <text x={434} y={249} fontSize="11" fill="#7B0D0D" fontWeight="700">Typical Manning's n values:</text>
        <text x={434} y={264} fontSize="11" fill="#444">Concrete: 0.012  |  Gravel: 0.025  |  Earth: 0.020–0.030</text>
        <text x={434} y={278} fontSize="11" fill="#444">Natural river (clean): 0.025–0.035  |  Floodplain grass: 0.030–0.060</text>
      </svg>
      <div style={S.dCaption}>Uniform flow occurs when the driving gravity force exactly balances bed friction — the water surface is parallel to the bed and depth is constant (normal depth yn). Manning's equation V = (1/n)R^(2/3)S₀^(1/2) is the workhorse of open channel hydraulics, where n is the roughness coefficient, R = A/P is the hydraulic radius, and S₀ is the bed slope. Originally empirical (Manning, 1889), it can be derived from the Darcy–Weisbach friction factor with f ∝ R^(−1/3). Normal depth is found by solving the implicit equation Q = (A/n)R^(2/3)S₀^(1/2) iteratively.</div>
    </div>
  );
}

// ── Diagram 7: Weirs, Gates & Controls ────────────────────────────
function DiagramControls() {
  const W = 900, H = 310;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Hydraulic Controls — Sharp-Crested Weir and Sluice Gate</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="wgArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="wgArrB" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#2980b9"/>
          </marker>
          <pattern id="wp6" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
            <line x1="0" y1="8" x2="8" y2="0" stroke="#aed6f1" strokeWidth="1.2" opacity="0.6"/>
          </pattern>
        </defs>

        {/* Left: Sharp-crested weir */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Sharp-Crested Rectangular Weir</text>

        {(()=>{
          const bx=20, bedY=250, bw=PW-20;
          const weirX=bw*0.5, weirH=90, P=80; // P = weir height above bed
          const H_head=70; // head over weir
          const ws_up = bedY - P - H_head;  // upstream water surface
          return <>
            {/* Bed */}
            <rect x={bx} y={bedY} width={bw} height={12} rx={2} fill="#c8a46e"/>
            {/* Weir body */}
            <rect x={bx+weirX-8} y={bedY-P-2} width={16} height={P+2} fill="#888" stroke="#555" strokeWidth="1.5"/>
            {/* Weir crest */}
            <line x1={bx+weirX-8} y1={bedY-P-2} x2={bx+weirX+8} y2={bedY-P-2} stroke="#555" strokeWidth="2.5"/>

            {/* Upstream water */}
            <path d={`M ${bx},${bedY} L ${bx},${ws_up} L ${bx+weirX-8},${ws_up} L ${bx+weirX-8},${bedY-P-2} L ${bx+weirX-8},${bedY} Z`}
              fill="url(#wp6)"/>
            <line x1={bx} y1={ws_up} x2={bx+weirX-8} y2={ws_up} stroke="#2980b9" strokeWidth="2"/>

            {/* Nappe (overfall jet) */}
            <path d={`M ${bx+weirX-8},${bedY-P-2} C ${bx+weirX+30},${bedY-P-2} ${bx+weirX+60},${bedY-P+15} ${bx+weirX+90},${bedY-P+50}`}
              fill="none" stroke="#2980b9" strokeWidth="2.5"/>
            <path d={`M ${bx+weirX+8},${bedY-P-2} C ${bx+weirX+38},${bedY-P+4} ${bx+weirX+68},${bedY-P+22} ${bx+weirX+96},${bedY-P+55}`}
              fill="none" stroke="#2980b9" strokeWidth="2"/>
            <text x={bx+weirX+55} y={bedY-P+10} fontSize="12" fill="#2980b9" fontWeight="700">Nappe</text>

            {/* Downstream water */}
            <path d={`M ${bx+weirX+8},${bedY} L ${bx+bw},${bedY} L ${bx+bw},${bedY-30} L ${bx+weirX+8},${bedY-30} Z`}
              fill="url(#wp6)" opacity="0.6"/>
            <line x1={bx+weirX+8} y1={bedY-30} x2={bx+bw} y2={bedY-30} stroke="#2980b9" strokeWidth="1.5" strokeDasharray="4,2"/>

            {/* Head annotation */}
            <line x1={bx+weirX-30} y1={bedY-P-2} x2={bx+weirX-30} y2={ws_up}
              stroke="#c0392b" strokeWidth="2" markerEnd="url(#wgArr)" markerStart="url(#wgArr)"/>
            <text x={bx+weirX-52} y={bedY-P-2-(H_head)/2+4} fontSize="12" fill="#c0392b" fontWeight="700">H</text>
            <line x1={bx+weirX-60} y1={bedY} x2={bx+weirX-60} y2={bedY-P}
              stroke="#555" strokeWidth="1.5" markerEnd="url(#wgArr)" markerStart="url(#wgArr)"/>
            <text x={bx+weirX-80} y={bedY-P/2+4} fontSize="11" fill="#555">P</text>

            {/* Formula */}
            <rect x={bx} y={H-74} width={bw} height={56} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
            <text x={bx+8} y={H-58} fontSize="12" fill="#7B0D0D" fontWeight="700">Q = (2/3) Cd · b · √(2g) · H^(3/2)</text>
            <text x={bx+8} y={H-42} fontSize="11" fill="#444">Cd ≈ 0.61–0.62  (discharge coefficient)</text>
            <text x={bx+8} y={H-28} fontSize="11" fill="#444">V-notch weir: Q = (8/15)Cd√(2g) tan(θ/2) H^(5/2)</text>
          </>;
        })()}

        {/* Right: Sluice gate */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Sluice Gate — Underflow Control</text>

        {(()=>{
          const bx=PW+30, bedY=250, bw=PW-20;
          const gateX=bw*0.45, gateOpen=45, ws_up=100;
          return <>
            {/* Bed */}
            <rect x={bx} y={bedY} width={bw} height={12} rx={2} fill="#c8a46e"/>
            {/* Upstream water */}
            <path d={`M ${bx},${bedY} L ${bx},${ws_up} L ${bx+gateX},${ws_up} L ${bx+gateX},${bedY} Z`}
              fill="url(#wp6)"/>
            <line x1={bx} y1={ws_up} x2={bx+gateX} y2={ws_up} stroke="#2980b9" strokeWidth="2.5"/>

            {/* Gate body */}
            <rect x={bx+gateX-6} y={ws_up-20} width={12} height={bedY-gateOpen-ws_up+20} rx={2} fill="#777" stroke="#555" strokeWidth="1.5"/>
            {/* Gate opening */}
            <line x1={bx+gateX-10} y1={bedY-gateOpen} x2={bx+gateX+10} y2={bedY-gateOpen}
              stroke="#e07b00" strokeWidth="2.5"/>
            <text x={bx+gateX+14} y={bedY-gateOpen+4} fontSize="11" fill="#e07b00" fontWeight="700">a (gate opening)</text>

            {/* Vena contracta downstream */}
            <path d={`M ${bx+gateX+6},${bedY} L ${bx+gateX+6},${bedY-gateOpen} C ${bx+gateX+50},${bedY-gateOpen-8} ${bx+gateX+80},${bedY-gateOpen*0.65} ${bx+bw},${bedY-gateOpen*0.62}`}
              fill="url(#wp6)"/>
            <path d={`M ${bx+gateX+6},${bedY-gateOpen} C ${bx+gateX+50},${bedY-gateOpen-8} ${bx+gateX+80},${bedY-gateOpen*0.65} ${bx+bw},${bedY-gateOpen*0.62}`}
              fill="none" stroke="#2980b9" strokeWidth="2.5"/>

            {/* Vena contracta marker */}
            <line x1={bx+gateX+60} y1={bedY-gateOpen*0.62-8} x2={bx+gateX+60} y2={bedY-gateOpen*0.62-22}
              stroke="#9b59b6" strokeWidth="1.5"/>
            <text x={bx+gateX+62} y={bedY-gateOpen*0.62-26} fontSize="11" fill="#9b59b6" fontWeight="700">Vena contracta</text>
            <text x={bx+gateX+62} y={bedY-gateOpen*0.62-12} fontSize="11" fill="#9b59b6">y₂ = Cc · a</text>

            {/* Head H */}
            <line x1={bx+gateX-28} y1={bedY} x2={bx+gateX-28} y2={ws_up}
              stroke="#c0392b" strokeWidth="2" markerEnd="url(#wgArr)" markerStart="url(#wgArr)"/>
            <text x={bx+gateX-48} y={(bedY+ws_up)/2+4} fontSize="12" fill="#c0392b" fontWeight="700">H₁</text>

            {/* Velocity arrows through gate */}
            {[bedY-38, bedY-28, bedY-18].map(y => (
              <line key={y} x1={bx+gateX+8} y1={y} x2={bx+gateX+60} y2={y}
                stroke="#c0392b" strokeWidth="2.2" markerEnd="url(#wgArr)"/>
            ))}

            <rect x={bx} y={H-74} width={bw} height={56} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
            <text x={bx+8} y={H-58} fontSize="12" fill="#7B0D0D" fontWeight="700">Q = Cd · a · b · √(2gH₁)</text>
            <text x={bx+8} y={H-42} fontSize="11" fill="#444">Cd = Cc / √(1 − (Cc·a/H₁)²)  ≈ 0.61</text>
            <text x={bx+8} y={H-28} fontSize="11" fill="#444">Cc (contraction coeff.) ≈ 0.61 (sharp edge gate)</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>Hydraulic controls are structures that fix a unique relationship between depth and discharge, setting boundary conditions for GVF profiles. A sharp-crested weir acts as an overflow control — upstream depth is controlled by the weir height and overfall head (Q ∝ H^(3/2) for rectangular, H^(5/2) for V-notch). A sluice gate acts as an underflow control — the jet contracts to the vena contracta at Cc·a below the gate, and Q ∝ √H₁. Both establish critical or near-critical flow locally, splitting the channel into independent upstream/downstream reaches.</div>
    </div>
  );
}

// ── Diagram 8: Channel Transitions — Humps & Constrictions ────────
function DiagramTransitions() {
  const W = 900, H = 300;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Channel Transitions — Bed Hump and Width Constriction</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="trArr" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#c0392b"/>
          </marker>
          <marker id="trArrG" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#27ae60"/>
          </marker>
          <pattern id="wp7" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
            <line x1="0" y1="8" x2="8" y2="0" stroke="#aed6f1" strokeWidth="1.2" opacity="0.6"/>
          </pattern>
        </defs>

        {/* Left: Bed hump in subcritical flow */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Bed Hump — Subcritical Flow</text>

        {(()=>{
          const bx=20, bw=PW-20, bedY=230;
          // Flat bed with hump
          const humpX=bw/2, humpH=40, humpW=80;
          const bumpPath = `M ${bx},${bedY} L ${bx+humpX-humpW},${bedY} C ${bx+humpX-humpW/2},${bedY} ${bx+humpX-humpW/4},${bedY-humpH} ${bx+humpX},${bedY-humpH} C ${bx+humpX+humpW/4},${bedY-humpH} ${bx+humpX+humpW/2},${bedY} ${bx+humpX+humpW},${bedY} L ${bx+bw},${bedY}`;
          // Water surface dips over hump (subcritical: E conserved, depth reduces)
          const y0=80, dy=25;
          const wsSurface = `M ${bx},${bedY-y0} L ${bx+humpX-humpW-10},${bedY-y0} C ${bx+humpX-humpW/2},${bedY-y0} ${bx+humpX},${bedY-humpH-(y0-dy)} ${bx+humpX+humpW/2},${bedY-y0+5} L ${bx+bw},${bedY-y0}`;
          return <>
            <path d={bumpPath + ` L ${bx+bw},${bedY+12} L ${bx},${bedY+12} Z`} fill="#c8a46e"/>
            <path d={bumpPath} fill="none" stroke="#8B6914" strokeWidth="2.5"/>
            {/* Water fill */}
            <path d={`${wsSurface} L ${bx+bw},${bedY} ` + bumpPath.replace('M','').split('L')[0] + ` Z`}
              fill="url(#wp7)" opacity="0.8"/>
            <path d={wsSurface} fill="none" stroke="#2980b9" strokeWidth="2.5"/>

            {/* Datum line */}
            <line x1={bx} y1={bedY} x2={bx+bw} y2={bedY} stroke="#555" strokeWidth="1" strokeDasharray="3,3" opacity="0.4"/>

            {/* Depth annotations */}
            <line x1={bx+30} y1={bedY} x2={bx+30} y2={bedY-y0}
              stroke="#27ae60" strokeWidth="1.5" markerEnd="url(#trArr)" markerStart="url(#trArr)"/>
            <text x={bx+34} y={bedY-y0/2+4} fontSize="11" fill="#27ae60">y₁</text>

            <line x1={bx+humpX+4} y1={bedY-humpH} x2={bx+humpX+4} y2={bedY-humpH-(y0-dy)}
              stroke="#c0392b" strokeWidth="1.5" markerEnd="url(#trArr)" markerStart="url(#trArr)"/>
            <text x={bx+humpX+10} y={bedY-humpH-(y0-dy)/2+4} fontSize="11" fill="#c0392b">y₂ &lt; y₁</text>

            {/* Total energy line (horizontal for ideal flow) */}
            <line x1={bx} y1={bedY-y0-20} x2={bx+bw} y2={bedY-y0-20}
              stroke="#e07b00" strokeWidth="1.5" strokeDasharray="6,3"/>
            <text x={bx+bw+4} y={bedY-y0-16} fontSize="11" fill="#e07b00">E₁ = const.</text>
            <text x={bx+bw+4} y={bedY-y0-2} fontSize="10" fill="#e07b00">(energy grade)</text>

            {/* Hump height */}
            <line x1={bx+humpX+humpW+14} y1={bedY} x2={bx+humpX+humpW+14} y2={bedY-humpH}
              stroke="#555" strokeWidth="1.2" markerEnd="url(#trArr)" markerStart="url(#trArr)"/>
            <text x={bx+humpX+humpW+20} y={bedY-humpH/2+4} fontSize="11" fill="#555">Δz</text>

            <rect x={bx} y={250} width={bw} height={40} rx={3} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1"/>
            <text x={bx+8} y={265} fontSize="11" fill="#145214" fontWeight="700">Specific energy: E₂ = E₁ − Δz</text>
            <text x={bx+8} y={281} fontSize="11" fill="#444">Depth decreases over hump; if Δz = ΔE_max → critical at crest (choking)</text>
          </>;
        })()}

        {/* Right: Width constriction */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Width Constriction — Plan View</text>

        {(()=>{
          const bx=PW+30, by=50, bw=PW-20, chanH=200;
          const cxMid=bx+bw/2;
          // Constriction walls
          const constW=50, constX=bw*0.45;
          const wallInset=55;
          return <>
            {/* Channel walls */}
            <rect x={bx} y={by} width={bw} height={chanH} fill="url(#wp7)" opacity="0.5"/>
            {/* Top wall */}
            <line x1={bx} y1={by} x2={bx+constX} y2={by} stroke="#555" strokeWidth="3"/>
            <path d={`M ${bx+constX},${by} C ${bx+constX+20},${by} ${bx+constX+30},${by+wallInset-10} ${bx+constX+constW/2},${by+wallInset}`}
              fill="none" stroke="#555" strokeWidth="3"/>
            <path d={`M ${bx+constX+constW/2},${by+wallInset} C ${bx+constX+constW/2+20},${by+wallInset+10} ${bx+constX+constW+20},${by+10} ${bx+constX+constW},${by}`}
              fill="none" stroke="#555" strokeWidth="3"/>
            <line x1={bx+constX+constW} y1={by} x2={bx+bw} y2={by} stroke="#555" strokeWidth="3"/>
            {/* Bottom wall */}
            <line x1={bx} y1={by+chanH} x2={bx+constX} y2={by+chanH} stroke="#555" strokeWidth="3"/>
            <path d={`M ${bx+constX},${by+chanH} C ${bx+constX+20},${by+chanH} ${bx+constX+30},${by+chanH-wallInset+10} ${bx+constX+constW/2},${by+chanH-wallInset}`}
              fill="none" stroke="#555" strokeWidth="3"/>
            <path d={`M ${bx+constX+constW/2},${by+chanH-wallInset} C ${bx+constX+constW/2+20},${by+chanH-wallInset-10} ${bx+constX+constW+20},${by+chanH-10} ${bx+constX+constW},${by+chanH}`}
              fill="none" stroke="#555" strokeWidth="3"/>
            <line x1={bx+constX+constW} y1={by+chanH} x2={bx+bw} y2={by+chanH} stroke="#555" strokeWidth="3"/>

            {/* Constriction fill (solid walls) */}
            <path d={`M ${bx+constX},${by} C ${bx+constX+20},${by} ${bx+constX+30},${by+wallInset-10} ${bx+constX+constW/2},${by+wallInset} C ${bx+constX+constW/2+20},${by+wallInset+10} ${bx+constX+constW+20},${by+10} ${bx+constX+constW},${by} Z`}
              fill="#999"/>
            <path d={`M ${bx+constX},${by+chanH} C ${bx+constX+20},${by+chanH} ${bx+constX+30},${by+chanH-wallInset+10} ${bx+constX+constW/2},${by+chanH-wallInset} C ${bx+constX+constW/2+20},${by+chanH-wallInset-10} ${bx+constX+constW+20},${by+chanH-10} ${bx+constX+constW},${by+chanH} Z`}
              fill="#999"/>

            {/* Streamlines */}
            {[0.2,0.4,0.6,0.8].map((frac,i) => {
              const y = by + frac*chanH;
              const mid_y_top = by + wallInset + (frac)*(chanH - 2*wallInset);
              return (
                <path key={i}
                  d={`M ${bx+10},${y} C ${bx+constX-20},${y} ${bx+constX+constW/2-10},${mid_y_top} ${bx+constX+constW+10},${y} C ${bx+constX+constW+40},${y} ${bx+bw-10},${y}`}
                  fill="none" stroke="#c0392b" strokeWidth="1.5" opacity="0.7"
                  markerEnd="url(#trArr)"/>
              );
            })}

            {/* Width labels */}
            <line x1={bx+10} y1={by-14} x2={bx+bw-10} y2={by-14}
              stroke="#555" strokeWidth="1.2" markerEnd="url(#trArr)" markerStart="url(#trArr)"/>
            <text x={bx+bw/2} y={by-22} textAnchor="middle" fontSize="11" fill="#555">b₁ (upstream width)</text>

            <line x1={bx+constX+constW/2} y1={by+wallInset-8} x2={bx+constX+constW/2} y2={by+chanH-wallInset+8}
              stroke="#c0392b" strokeWidth="1.5" markerEnd="url(#trArr)" markerStart="url(#trArr)"/>
            <text x={bx+constX+constW/2-24} y={by+chanH/2} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">b₂</text>

            {/* Labels */}
            <text x={bx+30} y={by+chanH/2+4} textAnchor="middle" fontSize="13" fill="#2980b9" fontWeight="700">V₁</text>
            <text x={bx+bw-30} y={by+chanH/2+4} textAnchor="middle" fontSize="13" fill="#27ae60" fontWeight="700">V₃</text>
            <text x={bx+constX+constW/2} y={by+chanH/2+4} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">V₂↑</text>

            <rect x={bx} y={250} width={bw} height={40} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
            <text x={bx+8} y={265} fontSize="11" fill="#7B0D0D" fontWeight="700">Continuity: Q = V₁b₁y₁ = V₂b₂y₂  (constant)</text>
            <text x={bx+8} y={281} fontSize="11" fill="#444">Constriction raises velocity; if critical → choking, upstream backed up</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>Channel transitions alter the specific energy (bed humps) or the flow width (constrictions). For a bed hump of height Δz: E₂ = E₁ − Δz — the operating point moves leftward on the specific energy curve, raising velocity and reducing depth in subcritical flow. A width constriction reduces the effective discharge per unit width, also consuming specific energy. When the required energy reduction exceeds the available specific energy above E_min, the flow chokes — critical depth is forced at the control section and the upstream depth must increase to supply the needed energy. This is the mechanism behind bridge afflux and dam backwater effects.</div>
    </div>
  );
}

// ── Diagram 9: Flood Routing & Natural River ───────────────────────
function DiagramFloodRouting() {
  const W = 900, H = 300;

  // Generate inflow/outflow hydrograph points
  const pts_in = [], pts_out = [];
  for (let t = 0; t <= 72; t += 1) {
    // Inflow: sharp triangular hydrograph
    const Qin = t < 24
      ? 50 + (350 - 50) * (t/24)
      : t < 48
        ? 350 - (350 - 80) * ((t-24)/24)
        : 80 - (80-30)*((t-48)/24);
    // Outflow: attenuated, delayed
    const delay = 8, att = 0.75;
    const td = t - delay;
    const Qout = td < 0 ? 30
      : td < 24 ? 50 + (350*att - 50)*( td/24)
      : td < 52  ? 350*att - (350*att - 70)*((td-24)/28)
      : 70 - (70-30)*((td-52)/20);
    pts_in.push({t, Q: Math.max(Qin, 30)});
    pts_out.push({t, Q: Math.max(Qout, 28)});
  }

  const tx = (t) => 80 + (t/72)*760;
  const qy = (Q) => 270 - ((Q-20)/360)*220;

  const inPath  = pts_in.map(({t,Q}, i) => `${i===0?'M':'L'} ${tx(t)},${qy(Q)}`).join(' ');
  const outPath = pts_out.map(({t,Q},i) => `${i===0?'M':'L'} ${tx(t)},${qy(Q)}`).join(' ');

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Flood Routing — Hydrograph Attenuation and Translation in a River Reach</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="frArr2" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#333"/>
          </marker>
        </defs>

        {/* Axes */}
        <line x1={80} y1={275} x2={860} y2={275} stroke="#333" strokeWidth="1.8" markerEnd="url(#frArr2)"/>
        <line x1={80} y1={275} x2={80}  y2={20}  stroke="#333" strokeWidth="1.8" markerEnd="url(#frArr2)"/>
        <text x={868} y={279} fontSize="12" fill="#333" fontWeight="700">t (hr)</text>
        <text x={60}  y={16}  fontSize="12" fill="#333" fontWeight="700">Q (m³/s)</text>

        {/* Ticks */}
        {[0,12,24,36,48,60,72].map(t => (
          <g key={t}>
            <line x1={tx(t)} y1={272} x2={tx(t)} y2={278} stroke="#333" strokeWidth="1.2"/>
            <text x={tx(t)} y={290} textAnchor="middle" fontSize="11" fill="#555">{t}</text>
          </g>
        ))}
        {[50,100,150,200,250,300,350].map(Q => (
          <g key={Q}>
            <line x1={77} y1={qy(Q)} x2={83} y2={qy(Q)} stroke="#333" strokeWidth="1.2"/>
            <text x={72} y={qy(Q)+4} textAnchor="end" fontSize="11" fill="#555">{Q}</text>
          </g>
        ))}

        {/* Fill between curves (volume stored) */}
        <path d={inPath + ' ' + outPath.split(' ').reverse().join(' ')} fill="#FEF0F0" opacity="0.4"/>

        {/* Curves */}
        <path d={inPath}  fill="none" stroke="#c0392b" strokeWidth="2.8"/>
        <path d={outPath} fill="none" stroke="#27ae60" strokeWidth="2.8"/>

        {/* Peak inflow annotation */}
        {(()=>{
          const peakIn  = pts_in.reduce((a,b) => a.Q > b.Q ? a : b);
          const peakOut = pts_out.reduce((a,b) => a.Q > b.Q ? a : b);
          return <>
            <line x1={tx(peakIn.t)}  y1={qy(peakIn.Q)}  x2={tx(peakIn.t)}  y2={275} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,3"/>
            <line x1={tx(peakOut.t)} y1={qy(peakOut.Q)} x2={tx(peakOut.t)} y2={275} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="4,3"/>
            <circle cx={tx(peakIn.t)}  cy={qy(peakIn.Q)}  r={5} fill="#c0392b"/>
            <circle cx={tx(peakOut.t)} cy={qy(peakOut.Q)} r={5} fill="#27ae60"/>

            {/* Translation arrow */}
            <line x1={tx(peakIn.t)} y1={qy(peakIn.Q)-20} x2={tx(peakOut.t)} y2={qy(peakIn.Q)-20}
              stroke="#e07b00" strokeWidth="2" markerEnd="url(#frArr2)" markerStart="url(#frArr2)"/>
            <text x={(tx(peakIn.t)+tx(peakOut.t))/2} y={qy(peakIn.Q)-28} textAnchor="middle" fontSize="11" fill="#e07b00" fontWeight="700">Translation (lag)</text>

            {/* Attenuation arrow */}
            <line x1={tx(peakOut.t)+14} y1={qy(peakOut.Q)} x2={tx(peakOut.t)+14} y2={qy(peakIn.Q)}
              stroke="#9b59b6" strokeWidth="2" markerEnd="url(#frArr2)" markerStart="url(#frArr2)"/>
            <text x={tx(peakOut.t)+20} y={(qy(peakOut.Q)+qy(peakIn.Q))/2+4} fontSize="11" fill="#9b59b6" fontWeight="700">Attenuation</text>
            <text x={tx(peakOut.t)+20} y={(qy(peakOut.Q)+qy(peakIn.Q))/2+18} fontSize="11" fill="#9b59b6">(peak reduced)</text>
          </>;
        })()}

        {/* Stored volume label */}
        <text x={tx(36)} y={qy(240)} textAnchor="middle" fontSize="12" fill="#c0392b" fontStyle="italic">Volume stored</text>
        <text x={tx(36)} y={qy(240)+15} textAnchor="middle" fontSize="12" fill="#c0392b" fontStyle="italic">in reach</text>

        {/* Legend */}
        <line x1={100} y1={36} x2={135} y2={36} stroke="#c0392b" strokeWidth="2.8"/>
        <text x={140} y={40} fontSize="12" fill="#c0392b">Inflow hydrograph (upstream)</text>
        <line x1={100} y1={56} x2={135} y2={56} stroke="#27ae60" strokeWidth="2.8"/>
        <text x={140} y={60} fontSize="12" fill="#27ae60">Outflow hydrograph (downstream) — attenuated &amp; delayed</text>

        {/* Method label */}
        <rect x={600} y={26} width={240} height={55} rx={3} fill="#fff5f5" stroke="#f0cece"/>
        <text x={606} y={42} fontSize="11" fill="#7B0D0D" fontWeight="700">Routing Methods:</text>
        <text x={606} y={56} fontSize="11" fill="#444">Muskingum: dS/dt = I − O,  S = K[xI+(1−x)O]</text>
        <text x={606} y={70} fontSize="11" fill="#444">Level-pool (reservoir): S–Q curve + continuity</text>
      </svg>
      <div style={S.dCaption}>Flood routing tracks how a flood wave changes in shape and timing as it travels through a river reach or reservoir. The river acts as temporary storage — the peak discharge is attenuated (reduced) and the hydrograph is translated (delayed) downstream. The difference between the inflow and outflow at any time equals the rate of change of storage in the reach (dS/dt = I − O). The Muskingum method captures this with two parameters: K (travel time) and x (weighting factor for storage). Accurate flood routing is essential for flood forecasting, reservoir spillway design, and floodplain mapping.</div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────
export default function OpenChannelFlow() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Open Channel Flow in Fluid Dynamics</h1>
      <p style={S.p}>
        Open channel flow is the flow of a liquid — almost always water — with a free surface exposed to the atmosphere. Rivers, canals, irrigation ditches, storm drains, and estuaries are all open channels. The defining feature is that the pressure at the free surface is everywhere atmospheric, so the driving force is gravity acting on the water's weight rather than a pressure gradient from an external pump or reservoir. This makes open channel flow qualitatively different from pipe flow: the depth adjusts freely in response to the balance between gravity, inertia, and friction, and a rich variety of flow phenomena — subcritical and supercritical regimes, hydraulic jumps, backwater curves, and choking — arise from this interplay.
      </p>

      <InfoBox title="Core Ideas at a Glance" items={[
        "Froude number Fr = V/√(gD) governs open channel flow — analogous to Mach number in compressible flow",
        "Subcritical (Fr < 1): deep, slow; downstream controls propagate upstream",
        "Supercritical (Fr > 1): shallow, fast; no information travels upstream",
        "Critical flow (Fr = 1): minimum specific energy for a given discharge; maximum discharge for a given energy",
        "Hydraulic jump: abrupt supercritical → subcritical transition; energy dissipated as turbulence (like a normal shock)",
        "Manning's equation: V = (1/n)R^(2/3)S₀^(1/2) — workhorse formula for uniform flow resistance",
        "GVF profiles (M1, M2, M3, S1, S2, S3…): gradually varied flow shapes behind weirs, gates, and slope changes",
        "Specific energy E = y + V²/(2g); two alternate depths for each E > E_min at fixed Q",
        "Hydraulic controls (weirs, gates, slope breaks) set boundary conditions and divide channels into independent reaches",
      ]}/>

      <h2 style={S.h2}>Channel Geometry and Hydraulic Properties</h2>
      <p style={S.p}>
        The geometry of an open channel cross-section determines all the hydraulic properties that govern resistance and flow capacity. The most important derived quantity is the hydraulic radius R = A/P — the ratio of flow area to wetted perimeter — which controls friction in the same way as pipe diameter in Poiseuille flow. For a wide rectangular channel (width ≫ depth), R ≈ y (the depth), simplifying many calculations. Natural channels are irregular and their properties must be surveyed; engineered channels are designed with specific cross-sections for maximum efficiency and constructability.
      </p>
      <DiagramCrossSections/>
      <Table2 rows={[
        ["Flow area A","Cross-sectional area of the flowing water (m²)\nDepends on depth y and cross-section shape"],
        ["Wetted perimeter P","Length of the channel boundary in contact with water (m)\nDoes not include the free surface — air exerts no friction"],
        ["Hydraulic radius R","R = A/P  (m) — the key length scale for resistance\nFor full circular pipe: R = D/4; wide channel: R ≈ y"],
        ["Top width T","Width of the free surface (m)\nVaries with depth for non-rectangular sections"],
        ["Hydraulic depth D","D = A/T  (m) — appears in Froude number\nFor rectangular channel: D = y (the depth)"],
        ["Section factor Z","Z = A√D = A^(3/2)/√T  (m^(5/2))\nUsed in critical flow calculations: Q = Z√g at critical flow"],
        ["Most efficient section","Maximises Q for a given A and n and S₀ (i.e., minimises P)\nSemicircle (theoretical best); trapezoidal half-hexagon (practical best)"],
        ["Freeboard","Extra height above design water surface in the channel bank\nTypically 15–30% of design depth to allow for waves and uncertainty"],
      ]}/>

      <h2 style={S.h2}>The Froude Number and Flow Regimes</h2>
      <p style={S.p}>
        The Froude number is to open channel flow what the Mach number is to compressible flow — it measures the ratio of flow speed to the speed at which small surface disturbances (gravity waves) propagate. A pebble dropped into subcritical flow sends ripples upstream and downstream; the same pebble in supercritical flow produces ripples that are swept entirely downstream. This asymmetry in information propagation is the most fundamental property of open channel hydraulics.
      </p>
      <DiagramFroudeRegimes/>
      <Table2 rows={[
        ["Froude number","Fr = V/√(gD)  (D = hydraulic depth = A/T)\nFor rectangular channel: Fr = V/√(gy)"],
        ["Wave celerity c","c = √(gD) — speed of small-amplitude gravity waves in still water\nFr < 1: V < c (waves travel upstream);  Fr > 1: V > c (waves swept downstream)"],
        ["Critical flow (Fr = 1)","V = c = √(gD);  minimum specific energy for given Q\nNaturally occurs at free overfalls, slope breaks, and channel constrictions"],
        ["Critical depth yc","Depth at which Fr = 1 for a given Q\nRectangular: yc = (q²/g)^(1/3);  general: Q²T/(gA³) = 1"],
        ["Critical slope Sc","Bed slope at which normal depth equals critical depth\nSc = gn²/(φ²R_c^(1/3))  (φ=1 in SI);  used to classify mild/steep slopes"],
        ["Mild slope (M)","S₀ < Sc → yn > yc → normal flow is subcritical (Fr < 1)\nMost natural rivers are mild-slope channels"],
        ["Steep slope (S)","S₀ > Sc → yn < yc → normal flow is supercritical (Fr > 1)\nOccurs on mountain streams, steep spillways, chutes"],
        ["Analogy with compressible flow","Subcritical ↔ subsonic; supercritical ↔ supersonic; hydraulic jump ↔ normal shock\nExpansion waves also exist: a channel widening is a Prandtl–Meyer-type expansion"],
      ]}/>

      <h2 style={S.h2}>Specific Energy and Alternate Depths</h2>
      <p style={S.p}>
        Specific energy E = y + V²/(2g) is the total mechanical energy per unit weight of fluid measured relative to the channel bed. Unlike total energy (which decreases downstream due to friction), specific energy changes only when the bed elevation changes or energy is added/extracted. The specific energy concept is the most powerful tool for analysing channel transitions — humps, constrictions, gates, and weirs — because it allows direct visualisation of how depth and velocity change as a flow navigates an obstacle.
      </p>
      <DiagramSpecificEnergy/>
      <Table2 rows={[
        ["Specific energy","E = y + V²/(2g) = y + Q²/(2gA²)  (energy per unit weight, relative to bed)\nMeasured in metres of water"],
        ["Minimum specific energy","E_min occurs at critical depth: E_min = (3/2)yc  (rectangular channel)\nThis is the minimum E for which a given Q can flow at all"],
        ["Alternate depths","For E > E_min and fixed Q, two depths exist: one subcritical, one supercritical\nThey carry the same discharge at the same specific energy"],
        ["Subcritical branch","Upper branch (y > yc): depth decreases as E decreases (hump raises, section constricts)\nDepth asymptotically approaches yc as E → E_min"],
        ["Supercritical branch","Lower branch (y < yc): depth increases as E increases\nLow depth, high velocity — sensitive to small perturbations"],
        ["Rectangular critical depth","yc = (q²/g)^(1/3);  Vc = √(g·yc);  Ec = (3/2)yc\nAll three are set by q alone — independent of channel slope or roughness"],
        ["Choking","If a transition requires more energy reduction than available (E₁ − Δz < E_min(Q))\nFlow cannot pass without modification — upstream depth rises (afflux)"],
        ["Discharge per unit width","q = Q/b  (m²/s) for rectangular channel\nKey parameter for 2D specific energy analysis; generalised to Z²g = Q² for other shapes"],
      ]}/>

      <h2 style={S.h2}>The Hydraulic Jump</h2>
      <p style={S.p}>
        The hydraulic jump is one of the most dramatic and practically important phenomena in open channel flow. It is the abrupt transition from supercritical to subcritical flow: a thin, shallow, high-speed flow suddenly expands into a deep, slow, turbulent mass of water with intense mixing, air entrainment, and energy dissipation. Like a normal shock wave, it is irreversible — momentum is conserved but a large fraction of kinetic energy is lost. Engineers exploit this to safely dissipate energy below spillways, weirs, and dam outlets.
      </p>
      <DiagramHydraulicJump/>
      <Table2 rows={[
        ["Sequent depth ratio","y₂/y₁ = ½(√(1+8Fr₁²) − 1)\nDerived from continuity + momentum (Belanger equation, 1828)\nFor Fr₁ = 3: y₂/y₁ = 3.77"],
        ["Energy loss","ΔE = (y₂−y₁)³/(4y₁y₂)\nAlways positive — confirms irreversibility\nFor Fr₁ = 3: ΔE/E₁ ≈ 28%"],
        ["Momentum function","M = Q²/(gA) + ȳA  (first moment of area about surface + velocity head)\nConserved across hydraulic jump (if no friction on walls)"],
        ["Jump types","Fr₁1.0–1.7: Undular (standing waves, little energy loss)\nFr₁ 1.7–2.5: Weak;  2.5–4.5: Oscillating;  4.5–9.0: Steady (best for stilling basin);  >9: Strong"],
        ["Stilling basin","Engineered flat floor downstream of a dam spillway where jump is anchored\nBaffle blocks and end sills shorten jump and prevent scour"],
        ["Jump length","Lj ≈ 5–7 × y₂  (empirical);  harder to predict than depth ratio\nImportant for sizing stilling basin"],
        ["Air entrainment","Turbulent roller entrains air — important for dissolved oxygen in rivers\nCan also cause cavitation damage in high-head structures"],
        ["Submerged jump","If tailwater depth > y₂: jump is pushed upstream and submerged\nEnergy dissipation reduced; dangerous in spillway design"],
      ]}/>

      <h2 style={S.h2}>Uniform Flow and Manning's Equation</h2>
      <p style={S.p}>
        Uniform flow is the simplest possible open channel flow: depth, velocity, and cross-section are constant along the channel, so the water surface is parallel to the bed and all driving and resisting forces are in balance. This occurs at the normal depth yn, which is the equilibrium depth for a given discharge and channel geometry. Manning's equation, despite being empirical, is remarkably accurate over a wide range of conditions and is the basis of virtually all practical open-channel design.
      </p>
      <DiagramUniformFlow/>
      <Table2 rows={[
        ["Uniform flow condition","dy/dx = 0: gravity component = friction force\nS₀ = Sf (bed slope = friction slope)"],
        ["Manning's equation","V = (1/n)·R^(2/3)·S₀^(1/2);  Q = (A/n)·R^(2/3)·S₀^(1/2)\nn = Manning's roughness coefficient (s/m^(1/3))"],
        ["Manning's n — typical values","Smooth concrete: 0.012;  Brickwork: 0.015;  Gravel earth: 0.025\nClean natural river: 0.025–0.035;  Mountain stream boulders: 0.040–0.070\nFloodplain with heavy brush: 0.070–0.160"],
        ["Normal depth yn","Depth at which uniform flow occurs at given Q\nImplicit — solve (A/n)R^(2/3)S₀^(1/2) = Q iteratively"],
        ["Chezy equation","V = C√(RS₀);  Chezy C = R^(1/6)/n  (SI units)\nHistorically earlier (1769); Manning gives C as function of R"],
        ["Darcy–Weisbach for channels","Sf = f·V²/(8gR);  f = 8gn²/R^(1/3) (from Manning equivalence)\nMore physically based; f depends on Re and relative roughness"],
        ["Conveyance K","K = (A/n)·R^(2/3);  Q = K·S₀^(1/2)\nConvenient for compound sections: K_total = ΣKi over subsections"],
        ["Compound channel","Floodplain + main channel: treat as separate subsections with different n\nCritical for flood inundation modelling"],
      ]}/>

      <h2 style={S.h2}>Gradually Varied Flow (GVF) Profiles</h2>
      <p style={S.p}>
        When depth changes slowly enough that streamlines are nearly parallel (small dA/dx), pressure is hydrostatic and the energy equation can be differentiated to give the GVF equation. This ODE describes how depth evolves along the channel when conditions deviate from uniform flow — behind a dam, above a weir, downstream of a gate, or wherever the slope changes. The twelve standard profile types (M, S, C, H, A) govern the practical design of almost every engineered waterway.
      </p>
      <DiagramGVFProfiles/>
      <Table2 rows={[
        ["GVF equation","dy/dx = (S₀ − Sf) / (1 − Fr²)\nS₀ = bed slope, Sf = friction slope (from Manning's), Fr = Froude number"],
        ["Backwater curve (M1)","y > yn > yc on mild slope: depth decreases upstream of dam/weir\nComputational: integrate upstream (subcritical) from control"],
        ["Drawdown curve (M2)","yn > y > yc: depth drops toward yc approaching a free overfall\nSurface dips; eventually critical at brink (yc ≈ 0.715 y_brink empirically)"],
        ["M3 profile","yn > yc > y: supercritical flow on mild slope below a gate\nTerminates in a hydraulic jump"],
        ["S1 profile","y > yc > yn on steep slope: deep slow water backed up behind obstruction\nSubcritical region above a dam on a steep channel"],
        ["S2 profile (drawdown)","yc > y > yn: on steep slope below a reservoir; accelerates to yn asymptotically"],
        ["Computational method","Standard step method: integrate energy equation reach by reach\nDirect step method: choose y, solve for Δx — simpler for prismatic channels"],
        ["Boundary conditions","Subcritical: set depth at downstream end, integrate upstream\nSupercritical: set depth at upstream end, integrate downstream"],
      ]}/>

      <h2 style={S.h2}>Hydraulic Controls — Weirs and Gates</h2>
      <p style={S.p}>
        Hydraulic controls are structures or channel features that impose a unique depth–discharge relationship at a specific location, thereby splitting the channel into independent upstream and downstream reaches. Weirs, sluice gates, culverts, bridge contractions, free overfalls, and slope breaks all act as controls. Understanding which sections are control points, and whether they are approached from subcritical (downstream control) or supercritical (upstream control) flow, is the foundation of practical channel design and flood analysis.
      </p>
      <DiagramControls/>
      <Table2 rows={[
        ["Sharp-crested weir","Q = (2/3)·Cd·b·√(2g)·H^(3/2)\nCd ≈ 0.611 (Francis formula);  H = head above crest\nRequires H/P < 0.5 for accuracy (negligible approach velocity)"],
        ["Broad-crested weir","Critical flow occurs on the crest: Q = 1.705·b·H^(3/2)\nMore robust (less sensitive to Cd); used for discharge measurement in rivers"],
        ["V-notch weir","Q = (8/15)·Cd·√(2g)·tan(θ/2)·H^(5/2)\nHighly sensitive at low flows; θ = 90° common (Cd ≈ 0.58)"],
        ["Sluice gate","Q = Cd·a·b·√(2gH₁);  Cd = Cc/√(1−(Cc·a/H₁)²) ≈ 0.61\na = gate opening, Cc ≈ 0.61 (sharp-edge), H₁ = upstream depth"],
        ["Vena contracta","Minimum cross-section of jet just downstream of gate/orifice\ny₂ = Cc·a  (Cc slightly less than 0.61 for bottom openings)"],
        ["Free overfall","Depth at brink y_b ≈ 0.715·yc (rectangular, free nappe)\nProvides unique Q(y) relationship — often used as measuring structure"],
        ["Drowned/submerged weir","Tailwater above crest level — discharge reduced\nQ_drowned = Q_free·f(H₂/H₁)  (Villemonte correction)"],
        ["Critical control section","Any point where Fr = 1 is imposed: gates, crests, slope breaks, constrictions\nDivides channel — GVF integration cannot cross a critical section"],
      ]}/>

      <h2 style={S.h2}>Channel Transitions — Humps and Constrictions</h2>
      <p style={S.p}>
        Transitions in bed elevation or channel width are encountered wherever flow passes over a submerged obstacle, through a bridge opening, or along a changing floodplain. The specific energy framework provides a clear graphical method: raising the bed by Δz reduces E₂ = E₁ − Δz, moving the operating point left on the E–y curve. If the transition is too large, the required energy reduction exceeds what is available above E_min — the flow chokes, critical depth is forced at the control, and the upstream depth must rise to supply additional energy.
      </p>
      <DiagramTransitions/>
      <Table2 rows={[
        ["Bed hump — subcritical","E₂ = E₁ − Δz: operating point moves left on upper (subcritical) branch\nDepth decreases, velocity increases; if Δz = E₁ − Emin: critical at crest"],
        ["Bed hump — supercritical","E₂ = E₁ − Δz: operating point moves left on lower branch\nDepth increases, velocity decreases; can trigger hydraulic jump downstream"],
        ["Width constriction — subcritical","Same Q in smaller width → higher q → higher E_min needed\nIf b₂ < b_critical: flow chokes — upstream depth rises (afflux)"],
        ["Bridge afflux","Rise in upstream water level caused by bridge piers and deck\nCritical for floodplain delineation and bridge design standards"],
        ["Maximum contraction","b_min = b₁·(Emin(Q)/E₁)^(3/2) for rectangular sections\nSmaller than this: flow chokes regardless of downstream conditions"],
        ["Transition losses","Real transitions have friction and separation losses: E₂ = E₁ − Δz − h_L\nExpansion losses typically larger than contraction losses"],
        ["Energy grade line","EGL = z_bed + y + V²/(2g) — slopes downward everywhere due to friction\nFor uniform flow: EGL slope = Sf = S₀"],
        ["Hydraulic grade line","HGL = z_bed + y — water surface elevation above datum\nEGL − HGL = V²/(2g)  (velocity head)"],
      ]}/>

      <h2 style={S.h2}>Flood Routing and Unsteady Flow</h2>
      <p style={S.p}>
        Real open channel flows are rarely steady — rainfall events, tide cycles, dam releases, and snowmelt all produce discharge that varies in time. The Saint-Venant equations (1871) are the complete description of one-dimensional unsteady open channel flow: conservation of mass and momentum with friction, bed slope, and lateral inflow terms. Flood routing predicts how a flood hydrograph changes shape and timing as it moves downstream through a river reach.
      </p>
      <DiagramFloodRouting/>
      <Table2 rows={[
        ["Saint-Venant equations","Continuity: ∂A/∂t + ∂Q/∂x = q_L (lateral inflow)\nMomentum: ∂Q/∂t + ∂(Q²/A)/∂x + gA·∂y/∂x = gA(S₀ − Sf)"],
        ["Kinematic wave","Sf ≈ S₀: inertia and pressure terms small — friction dominates\nQ = Q(A) only; wave translates without distortion; simplest routing"],
        ["Diffusion wave","Adds pressure term (∂y/∂x): wave attenuates and disperses\nGood for gradually rising floods in mild rivers; still neglects inertia"],
        ["Dynamic (full Saint-Venant)","All terms retained: captures backwater, tidal influence, dam-break waves\nSolved numerically (finite difference, finite volume)"],
        ["Muskingum method","dS/dt = I − O;  S = K[xI + (1−x)O]\nK = wave travel time (hr);  x = 0–0.5 (routing coefficient)\nWidely used, simple, only two parameters to calibrate"],
        ["Level-pool routing","For reservoirs: S = f(h),  Q = f(h) — both functions of stage\nSolve simultaneously with dS/dt = I − O  (Puls method)"],
        ["Flood wave speed","c_w = dQ/dA = V + √(gD)  (kinematic wave)\nFor subcritical flow: flood wave moves faster than mean flow velocity"],
        ["Flood attenuation","Peak flow reduces downstream due to storage in reach (river valley)\nRatio Q_peak_out/Q_peak_in depends on catchment and channel geometry"],
      ]}/>

      <h2 style={S.h2}>Sediment Transport and Scour</h2>
      <Table2 rows={[
        ["Shields parameter","τ* = τ_b/(ρ_s − ρ)gd\nτ_b = bed shear stress = ρgRS_f;  d = grain diameter\nThreshold for motion: τ* ≈ 0.047 (Shields criterion)"],
        ["Bed shear stress","τ_b = ρgRS₀  (uniform flow)\nFor gravel rivers: τ_b > τ_c (critical shear) → bedload transport begins"],
        ["Bedload transport","q_b = 8(τ* − τ*_c)^(1.5) · √((ρ_s/ρ−1)g·d³)  (Meyer-Peter & Müller)\nSand and gravel sliding/rolling along bed"],
        ["Suspended load","Fine sediment (silt, clay) carried in suspension by turbulence\nRouse number Z = w_s/(κu*) < 1.2 for significant suspension"],
        ["Shields diagram","Critical τ* vs grain Reynolds number Re* = u*d/ν\nShows transition from viscous to turbulent threshold regime"],
        ["Local scour","Erosion around bridge piers and abutments — common cause of bridge failure\nHEC-18 formula: ys/y₁ = 2.0·K₁K₂K₃K₄·(y₁/a)^(0.35)·Fr₁^(0.43)"],
        ["General scour","Channel-wide bed lowering during floods due to increased shear stress\nDistinct from local scour — affects entire reach"],
        ["Armouring","Coarse surface layer left after fine material washed away\nProtects underlying sediment; armoured channels have higher effective roughness"],
      ]}/>

      <h2 style={S.h2}>Engineering Applications</h2>
      <Table2 rows={[
        ["Irrigation canals","Designed for uniform flow at normal depth; lined (n≈0.014) or unlined (n≈0.025)\nFroude number kept below 0.5 to prevent bank erosion"],
        ["Spillways and chutes","High-velocity supercritical flow on steep slope; stilling basin at base\nAir entrainment and cavitation design critical above 10–15 m/s"],
        ["Culverts","Pipes or box structures under roads carrying streams\nMay flow full (pipe flow) or part-full (open channel) depending on headwater depth"],
        ["Natural river restoration","Remeandering, re-grading, and riparian planting to restore hydraulic and ecological function\nTarget: bankfull discharge at moderate Fr with natural sediment regime"],
        ["Urban drainage","Storm sewers must handle 10–100 yr return period events\nPermeable pavements and detention basins attenuate peak flows"],
        ["Tidal channels and estuaries","Unsteady flow with tidal forcing — both flood and ebb tides\nSalinity stratification; salt intrusion modelled with Saint-Venant + advection–diffusion"],
        ["Hydroelectric power","Intake structure draws subcritical reservoir flow into penstock\nTailrace returns supercritical draft-tube discharge to river"],
        ["Flood inundation mapping","2D Saint-Venant models on GIS terrain + LiDAR beds\nUsed for floodplain delineation, insurance, emergency response planning"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Froude number:  Fr = V/√(gD),  D = A/T  (hydraulic depth)",
        "Critical depth (rectangular):  yc = (q²/g)^(1/3);  q = Q/b  (unit discharge)",
        "Minimum specific energy:  Emin = (3/2)yc  (rectangular channel)",
        "Manning's equation:  V = (1/n)·R^(2/3)·S₀^(1/2);  Q = A·V",
        "Hydraulic radius:  R = A/P  (A = flow area, P = wetted perimeter)",
        "Hydraulic jump sequent depths:  y₂/y₁ = ½(√(1+8Fr₁²) − 1)",
        "Hydraulic jump energy loss:  ΔE = (y₂ − y₁)³ / (4y₁y₂)",
        "GVF equation:  dy/dx = (S₀ − Sf) / (1 − Fr²)",
        "Sharp-crested weir:  Q = (2/3)·Cd·b·√(2g)·H^(3/2),  Cd ≈ 0.61",
        "Sluice gate:  Q = Cd·a·b·√(2gH₁),  Cd ≈ 0.61",
        "Shields threshold:  τ* = τb/((ρs−ρ)gd) ≈ 0.047 for incipient motion",
      ]}/>
    </div>
  );
}